import { useState } from 'react'
import { Loader2, ScanLine } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { Button, Field, Input } from '../ui/kit'
import { scanneAusweis, verfuegbar, type ScanFelder } from '../lib/ausweisScan'

/**
 * Einen Ausweis scannen und die Person in den Meldeschein eintragen.
 *
 * Aus Sicht des VERMIETERS, bei der Anreise. Der Gast, der vorab ausfüllt, hat
 * seinen Ausweis meist nicht zur Hand; der Vermieter an der Rezeption hat ihn in
 * der Hand — das ist der Moment, in dem ein Scan Zeit spart. Begründung in
 * `docs/adr/004`.
 *
 * Erscheint nur in der App: ML Kit gibt es nur nativ, und die Gastseite soll
 * weder ein größeres Bündel noch einen Kameradialog bekommen.
 */

interface Person {
  first_name: string
  last_name: string
  birth_date: string
  nationality: string
  street: string
  postcode: string
  city: string
  country: string
  document_type: string
  document_number: string
}

const leer = (): Person => ({
  first_name: '', last_name: '', birth_date: '', nationality: '',
  street: '', postcode: '', city: '', country: '',
  document_type: '', document_number: '',
})

export default function AusweisScanPanel({
  registrationId, onSaved,
}: {
  registrationId: string
  onSaved: () => void
}) {
  const { lang } = useI18n()
  const de = lang === 'de'
  const store = useStore()

  const [offen, setOffen] = useState(false)
  const [person, setPerson] = useState<Person>(leer)
  const [scannt, setScannt] = useState(false)
  const [busy, setBusy] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  /** Welche Felder der Vermieter selbst angefasst hat. Siehe `uebernehmen`. */
  const [vonHand, setVonHand] = useState<Set<keyof Person>>(new Set())

  if (!verfuegbar()) return null

  const setzen = (k: keyof Person, v: string) => {
    setPerson((p) => ({ ...p, [k]: v }))
    setVonHand((s) => new Set(s).add(k))
  }

  /*
   * Das Getippte gewinnt (AK4).
   *
   * Wer ein Feld von Hand korrigiert hat — weil die Erkennung Unsinn lieferte
   * oder weil im Pass ein anderer Name steht als im Alltag —, dessen Eingabe
   * darf ein zweiter Scan nicht überschreiben. Sonst korrigiert der Vermieter
   * zweimal dasselbe und merkt beim dritten Mal nicht, dass es zurückgesprungen
   * ist.
   */
  const uebernehmen = (felder: ScanFelder) => {
    setPerson((p) => {
      const neu = { ...p }
      for (const [k, v] of Object.entries(felder)) {
        const feld = k as keyof Person
        if (!vonHand.has(feld) && v) neu[feld] = v
      }
      return neu
    })
  }

  const scannen = async () => {
    setFehler(null); setHinweis(null); setScannt(true)
    try {
      const r = await scanneAusweis()
      if (!r.erkannt) {
        setHinweis(de
          ? 'Kein Ausweis erkannt. Die zwei Zeilen ganz unten müssen vollständig im Bild sein — flach hinlegen hilft.'
          : 'No document recognised. The two lines at the very bottom must be fully in frame — laying it flat helps.')
      } else {
        uebernehmen(r.felder)
        setHinweis(r.vollstaendig
          ? (de ? 'Gelesen und geprüft.' : 'Read and verified.')
          : (de
            ? 'Teilweise gelesen. Was die Prüfziffer nicht bestanden hat, blieb leer — bitte ergänzen.'
            : 'Partially read. Anything that failed its check digit was left blank — please fill it in.'))
      }
    } catch {
      // Abbruch oder verweigerte Berechtigung ist kein Fehler, sondern eine
      // Entscheidung des Benutzers (AK5).
      setHinweis(null)
    }
    setScannt(false)
  }

  const speichern = async () => {
    setBusy(true); setFehler(null)
    try {
      if (!supabase) throw new Error('no_backend')
      if (!person.first_name.trim() && !person.last_name.trim()) {
        throw new Error(de ? 'Ohne Namen lässt sich niemand melden.' : 'A name is required.')
      }
      const { error } = await supabase.from('guest_registration_people').insert({
        registration_id: registrationId,
        tenant_id: store.db.tenant.id,
        sort_order: 0,
        ...person,
        birth_date: person.birth_date || null,
      })
      if (error) throw error
      setPerson(leer()); setVonHand(new Set()); setHinweis(null); setOffen(false)
      onSaved()
    } catch (e) {
      setFehler(String((e as { message?: string })?.message ?? e))
    }
    setBusy(false)
  }

  if (!offen) {
    return (
      <Button size="sm" variant="secondary" onClick={() => setOffen(true)}>
        <ScanLine size={15} />
        {de ? 'Ausweis scannen' : 'Scan a document'}
      </Button>
    )
  }

  return (
    <div className="bg-surface rounded-xl p-3 space-y-2.5">
      <Button size="sm" variant="primary" disabled={scannt} onClick={scannen}>
        {scannt ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />}
        {de ? 'Ausweis vor die Kamera' : 'Hold the document to the camera'}
      </Button>

      {/* AK6: wer eine Kamera auf einen Ausweis richten soll, will wissen, wohin
          das Bild geht. */}
      <p className="text-xs text-muted">
        {de
          ? 'Gelesen wird auf diesem Gerät. Das Bild wird nicht übertragen und nach dem Lesen gelöscht — übernommen werden nur die Angaben.'
          : 'Reading happens on this device. The image is not transmitted and is deleted afterwards — only the details are kept.'}
      </p>

      {hinweis && <p className="text-xs text-accent">{hinweis}</p>}

      <div className="grid grid-cols-2 gap-2">
        <Field label={de ? 'Vorname' : 'First name'}>
          <Input value={person.first_name} onChange={(e) => setzen('first_name', e.target.value)} />
        </Field>
        <Field label={de ? 'Nachname' : 'Last name'}>
          <Input value={person.last_name} onChange={(e) => setzen('last_name', e.target.value)} />
        </Field>
        <Field label={de ? 'Geburtsdatum' : 'Date of birth'}>
          <Input type="date" value={person.birth_date}
            onChange={(e) => setzen('birth_date', e.target.value)} />
        </Field>
        <Field label={de ? 'Staatsangehörigkeit' : 'Nationality'}>
          <Input value={person.nationality} onChange={(e) => setzen('nationality', e.target.value)} />
        </Field>
        <Field label={de ? 'Ausweisart' : 'Document type'}>
          <Input value={person.document_type} onChange={(e) => setzen('document_type', e.target.value)} />
        </Field>
        <Field label={de ? 'Ausweisnummer' : 'Document number'}>
          <Input value={person.document_number}
            onChange={(e) => setzen('document_number', e.target.value)} />
        </Field>
      </div>

      {/* Die Anschrift steht NICHT in der MRZ — sie muss immer getippt werden.
          Das steht so in der Anforderung und soll niemanden überraschen. */}
      <p className="text-xs text-muted">
        {de ? 'Die Anschrift steht nicht im Ausweis-Code und muss eingetragen werden.'
            : 'The address is not part of the document code and has to be entered.'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Field label={de ? 'Straße' : 'Street'}>
          <Input value={person.street} onChange={(e) => setzen('street', e.target.value)} />
        </Field>
        <Field label={de ? 'PLZ' : 'Postcode'}>
          <Input value={person.postcode} onChange={(e) => setzen('postcode', e.target.value)} />
        </Field>
        <Field label={de ? 'Ort' : 'City'}>
          <Input value={person.city} onChange={(e) => setzen('city', e.target.value)} />
        </Field>
        <Field label={de ? 'Land' : 'Country'}>
          <Input value={person.country} onChange={(e) => setzen('country', e.target.value)} />
        </Field>
      </div>

      {fehler && <p className="text-xs text-danger">{fehler}</p>}

      <div className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => { setOffen(false); setPerson(leer()); setVonHand(new Set()) }}>
          {de ? 'Abbrechen' : 'Cancel'}
        </Button>
        <Button size="sm" variant="primary" disabled={busy} onClick={speichern}>
          {busy && <Loader2 size={15} className="animate-spin" />}
          {de ? 'Person eintragen' : 'Add person'}
        </Button>
      </div>
    </div>
  )
}
